import { useContext, useEffect, useState } from 'react';
import {
  control,
  ControlPosition,
  data,
  HtmlMarker,
  layer,
  source
} from 'azure-maps-control';
import { AzureMapsContext } from 'react-azure-maps';

import { boundingBoxNetherlands } from './constants';
import {
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  DARK,
  GRAY,
  GRAY_SECONDARY,
  MINT,
  ORANGE,
  ORANGE_SECONDARY,
  PRIMARY,
  RED,
  SECONDARY,
  TURQUOISE,
  YELLOW
} from 'constants/colors';

import { Box, styled } from '@mui/material';
import MapComponent from 'components/shared/Map/MapComponent';
import cleanMap from 'components/shared/Map/cleanMap';

import type { FC } from 'react';
import type { IAzureMapsContextProps } from 'react-azure-maps';
import type { LatitudeLongitudePair } from 'components/roster-portal/activity-planning/types';

const StyledMapComponent = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  '.pin, .pin-to-plan': {
    width: '30px',
    height: '30px',
    borderRadius: '50% 50% 50% 0',
    transform: 'rotate(-45deg)',
    left: '50px',
    top: '50px',
    margin: '-20px 0 0 -20px',
    backgroundSize: 'cover',
    overflow: 'hidden'
  },
  '.pin': {
    border: '3px solid ' + PRIMARY,
    backgroundColor: PRIMARY
  },
  '.pin-to-plan': {
    border: '3px solid ' + ORANGE,
    backgroundColor: ORANGE
  },
  '.pin img, .pin-to-plan img': {
    transform: 'rotate(45deg)',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '.pin:hover': {
    cursor: 'pointer'
  },

  '.number': {
    position: 'absolute',
    top: '45%',
    left: '0%',
    transform: 'rotate(45deg)',
    fontSize: '10px',
    color: 'rgb(0, 0, 0)',
    fontFamily: '"Heebo", sans-serif',
    textShadow:
      '-0.5px 0.5px 0 white, 0.5px 0.5px 0.5px white, 0.5px -0.5px 0 white, -0.5px -0.5px 0 white'
  }
}));

const ROUTE_COLORS = [
  PRIMARY,
  SECONDARY,
  ORANGE,
  BLUE,
  ORANGE_SECONDARY,
  BLUE_DARK,
  TURQUOISE,
  MINT,
  BLUE_LIGHT,
  DARK,
  YELLOW,
  RED,
  GRAY,
  GRAY_SECONDARY
];

type AzureMap = NonNullable<IAzureMapsContextProps['mapRef']>;

const addMarker = (mapRef: AzureMap, position: number[]) => {
  mapRef.markers.add(
    new HtmlMarker({
      position,
      htmlContent: `<div><div class='pin'></div></div>`
    })
  );
};

const addMarkers = (mapRef: AzureMap, positions: number[][]) => {
  if (positions.length === 0) {
    return;
  }

  addMarker(mapRef, positions[0]);
  if (positions.length > 1) {
    addMarker(mapRef, positions[positions.length - 1]);
  }
};

const drawRoutes = (
  mapRef: AzureMap,
  datasource: source.DataSource,
  pointSet: LatitudeLongitudePair[][]
) => {
  datasource.clear();
  mapRef.markers.clear();

  if (pointSet.length === 0) {
    return;
  }

  const routeFeatures: data.Feature<data.LineString, { color: string }>[] = [];

  pointSet.forEach((points, i) => {
    const positions = points.map((p) => [p.longitude, p.latitude]);
    if (positions.length === 0) {
      return;
    }

    const features = [
      new data.Feature(new data.LineString(positions), {
        color: ROUTE_COLORS[i]
      })
    ];
    datasource.add(features);
    routeFeatures.push(features[0]);
    addMarkers(mapRef, positions);
  });

  mapRef.setCamera({
    bounds:
      routeFeatures.length > 0
        ? data.BoundingBox.fromData(routeFeatures)
        : boundingBoxNetherlands,
    padding: 50
  });
};

interface Props {
  pointSet: LatitudeLongitudePair[][];
}

/** A data source is only usable together with the map it was added to. */
interface MapSetup {
  map: AzureMap;
  datasource: source.DataSource;
}

const MapController: FC<Props> = ({ pointSet }) => {
  'use memo';

  const { mapRef, isMapReady } =
    useContext<IAzureMapsContextProps>(AzureMapsContext);

  const [setup, setSetup] = useState<MapSetup>();

  // Set the map up once, as soon as it is ready. This must not depend on the
  // routes: that would add another zoom control, data source and line layer on
  // top of the existing ones every time they change.
  useEffect(() => {
    if (!isMapReady || !mapRef) {
      return;
    }

    const zoomControl = new control.ZoomControl();

    /* Add the zoom control to the map*/
    mapRef.controls.add(zoomControl, {
      position: ControlPosition.BottomRight
    });

    const datasource = new source.DataSource();
    mapRef.sources.add(datasource);

    const lineLayer = new layer.LineLayer(datasource, 'line', {
      strokeColor: ['get', 'color'],
      strokeWidth: 5,
      lineJoin: 'round',
      lineCap: 'round'
    });
    mapRef.layers.add(lineLayer);

    // Drawing is left to the effect below, which runs as soon as this lands.
    setSetup({ map: mapRef, datasource });

    // Take everything off again when the map instance is replaced, see cleanMap.
    return () => {
      cleanMap(mapRef, { lineLayer, datasource, zoomControl });
      setSetup(undefined);
    };
  }, [isMapReady, mapRef]);

  // Both callers build a new pointSet array on every render (MapView passes
  // `[points]`, TravelDetailDialog maps over its routes), so redrawing on the
  // array identity would clear the map and refit the camera on every render —
  // throwing away the pan and zoom the user just did. Serializing the routes
  // and depending on that string instead redraws only when they really change.
  const serializedPointSet = JSON.stringify(pointSet ?? []);

  useEffect(() => {
    if (setup) {
      drawRoutes(
        setup.map,
        setup.datasource,
        JSON.parse(serializedPointSet) as LatitudeLongitudePair[][]
      );
    }
  }, [setup, serializedPointSet]);

  return (
    <StyledMapComponent>
      <MapComponent />
    </StyledMapComponent>
  );
};

export default MapController;
